/**
 * Sessions on the person's own computer, behind the same seam as the cloud's:
 * each conversation call becomes one of the client's calls to the local
 * OpenCode server, the snapshot is rebuilt from OpenCode's messages and the
 * live stream is numbered by the session's feed. A call that fails throws
 * the failure as `local-errors.ts` reads it: plain words, and the kind the
 * page's Retry is decided by.
 */
import type { ApprovalMode } from '@/lib/code-sessions/approval-mode';
import type { OpenCodeEvent } from '@/lib/code-sessions/local-agent/opencode-api';
import type {
  ListedSession,
  LocalSessionSnapshot,
  LocalSessionSummary,
} from '@/lib/code-sessions/local-agent/operations';
import type { FileSearchPlace, WorkspaceSearchResult } from '@/lib/code-sessions/workspace-types';
import type { SessionInboxCompaction, SessionInboxUser } from '@opencode/client/promise';

import type {
  CodePromptAttachment,
  CodeSessionRow,
  CodeSessionSummary,
  CodeSessionTurn,
  SessionResponse,
} from '../../types';
import type {
  CodeRuntime,
  ReadOptions,
  SavedSessionRow,
  SessionConversation,
  SessionRecord,
  SessionWorkspace,
} from '../types';
import type { LocalAgentConnection } from './connection';

import { UnconfirmedPromptError } from '../request-error';
import { isPermissionReply } from './approvals';
import { localAgentConnection } from './connection';
import { localFailure, sessionElsewhere, toLocalRequestError } from './local-errors';
import { forgetLocalSessionFeed, localSessionFeed } from './local-events';
import { refreshLocalList } from './local-list';
import { LOCAL_AGENT_STARTING, startAgainFirst } from './local-retry';
import {
  folderName,
  heldPromptImages,
  isoTime,
  LOCAL_SESSION_AUTHOR_ID,
  localActivityAt,
  localPromptId,
  localRewindTurnId,
  localSessionResponse,
  localImageRoom,
  localSessionRow,
  localStatus,
  localTurnRows,
  sentTurn,
  untitledSessionName,
  withUnsentTurns,
} from './local-snapshot';

import { clientLogger } from '@/lib/client-logger';
import { isCompactPrompt } from '@/lib/code-sessions/compaction';
import {
  OpenCodeHttpError,
  OpenCodeTimeoutError,
} from '@/lib/code-sessions/local-agent/opencode-api';
import { codeSkillsOf } from '@/lib/code-sessions/skills';
import { cutInForWaitingSteers } from '@/lib/code-sessions/steer-cut-in';
import { codeSessionPromptTitle, normalizeCodeSessionTitle } from '@/lib/code-sessions/title';

/**
 * OpenCode keeps the first copy of a prompt's message id: sent again in the same session, the
 * prompt gets that copy back, so it runs once. It answers 409 only when another copy was
 * delivered while this one was being taken in: the turn was sent then too.
 */
function isAlreadySent(error: unknown): boolean {
  return error instanceof OpenCodeHttpError && error.status === 409;
}

/**
 * A call's failure as the seam's callers read it (`local-errors.ts`): plain
 * words, and a status below 500 for the server's own answer, which a Retry
 * would not change. A session the server no longer has is gone from the
 * sidebar's list too, so the list is told to read again.
 */
async function request<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    const failure = toLocalRequestError(error);
    if (failure === null) {
      throw error;
    }
    if (failure.kind === 'gone') {
      refreshLocalList();
    }
    throw failure;
  }
}

/**
 * The snapshot's raw tail, when it still starts at the turn's start. A tail
 * that lost its oldest events to the stored tail's cap (`stored-tail.ts`)
 * would rebuild text in progress with a hole, so the page shows the stored
 * messages alone and lets the live events, or the next snapshot, fill the
 * turn in. A tail whose turn succeeded since it began, as one that ended
 * while the page reloaded, goes too: the messages hold that turn whole, as the
 * live `session.execution.succeeded` that drops a tail says.
 */
function usableTail(snapshot: LocalSessionSnapshot): readonly OpenCodeEvent[] {
  const began = snapshot.events[0];
  const succeededSince =
    began?.type === 'session.execution.started' &&
    snapshot.messages.some(
      (message) =>
        message.type === 'idle' &&
        message.outcome === 'succeeded' &&
        message.time.created >= began.created,
    );
  return snapshot.eventsTruncated || succeededSince ? [] : snapshot.events;
}

/**
 * A `data:` URL of bytes this tab holds, as OpenCode's own composer reads an image it sends:
 * OpenCode decodes only canonical base64 (`decodeDataURL` in `packages/core/src/session/prompt.ts`).
 * The URL names `contentType`, since a pasted or dropped file can come with no type of its own.
 */
function dataUrlOf(file: Blob, contentType: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Could not read the image to send'));
      }
    });
    reader.addEventListener('error', () => reject(reader.error));
    reader.readAsDataURL(new Blob([file], { type: contentType }));
  });
}

async function promptFile({ name, file, dataUrl, contentType }: CodePromptAttachment) {
  if (file !== undefined) {
    return { uri: await dataUrlOf(file, contentType), name };
  }
  if (dataUrl !== undefined) {
    return { uri: dataUrl, name };
  }
  throw new Error(`${name} can no longer be sent; attach it again`);
}

async function promptFiles(attachments: readonly CodePromptAttachment[]) {
  return attachments.length === 0 ? undefined : Promise.all(attachments.map(promptFile));
}

function connected(): LocalAgentConnection {
  const connection = localAgentConnection();
  if (connection === null) {
    throw sessionElsewhere();
  }
  return connection;
}

function notLocal(what: string): never {
  throw localFailure(`${what} is not available for a session on this computer`, 400);
}

function cutInForSteers(connection: LocalAgentConnection, sessionId: string): Promise<boolean> {
  return cutInForWaitingSteers({
    waiting: () => connection.inbox(sessionId),
    interruptAndResume: () => connection.interrupt(sessionId, { resume: true }),
    // OpenCode wakes for a prompt sent again under its id, held or already delivered, and keeps the
    // copy it has. Sending a steer of this cut-in touches no queued prompt, so a Remove or an Undo
    // that took one out meanwhile stays done.
    wakeQueue: async (waiting) => {
      const steer = waiting.find(
        (item): item is SessionInboxUser => item.type === 'user' && item.delivery === 'steer',
      );
      if (steer !== undefined) {
        await connection.prompt(sessionId, {
          messageId: steer.id,
          text: steer.payload.text,
          delivery: 'steer',
        });
      }
    },
  });
}

/**
 * The first prompt of each session this page started on this computer, or read before OpenCode
 * titled it, as it names the session (`codeSessionPromptTitle`), until a list read finds
 * OpenCode's title for it. Until then its first prompt names it, as the cloud's does, and
 * OpenCode's list carries no prompt: without this, the row the create seeded, or the one a read
 * wrote into the list (`localLists.update`), would give way to the folder at the list's next read.
 */
const firstPrompts = new Map<string, string>();

function localConversation(sessionId: string): SessionConversation {
  const feed = localSessionFeed(sessionId);
  const snapshot = (options?: ReadOptions) =>
    request(async () => {
      options?.signal?.throwIfAborted();
      const checkpoint = feed.checkpoint();
      const taken = await connected().getSnapshot(sessionId);
      options?.signal?.throwIfAborted();
      const turns = localTurnRows(taken);
      // The page names an untitled session by its first turn (`localSessionRow`); the list names
      // it the same way until OpenCode titles it.
      const first = turns[0];
      if (taken.session.title === undefined && first !== undefined) {
        firstPrompts.set(sessionId, codeSessionPromptTitle(first.prompt, first.promptAttachments));
      }
      const tail = feed.prime(usableTail(taken), turns, checkpoint);
      // A prompt this page sent after the read began is in no inbox the read saw: its row rides
      // along, so the tray keeps the person's words until a later read places it. A send that gave
      // up before the read began and that the read lacks never reached OpenCode: its row is a
      // failed send, with its words and Retry, in the place the person sent it.
      const listed = new Set(turns.map((turn) => turn.id));
      return localSessionResponse(
        taken,
        withUnsentTurns(
          [...turns, ...feed.sentSince(checkpoint, listed)],
          feed.unsentBefore(checkpoint),
        ),
        tail,
      );
    });
  /** The row a steer or cancel returns: recovered before mutation so its prompt and original metadata survive cancellation. */
  const rowFor = async (turnId: string): Promise<CodeSessionTurn> => {
    const known = feed.rowOf(turnId);
    if (known !== undefined) {
      return known;
    }
    await snapshot();
    const row = feed.rowOf(turnId);
    if (row === undefined) {
      throw localFailure('This turn is no longer available on this computer', 409);
    }
    return row;
  };
  return {
    snapshot,
    // A hover's prefetch would read the whole session only for the view to load it again when it
    // opens, since a feed it primed cannot be trusted after a gap with no replay: nothing to
    // cache, so the open reads once.
    prefetch: async () => null,
    // The transcript banner's Retry: a failed read may follow a server that stopped, so start()
    // runs again first, as the toasts' and the whole page's Retry do, and the read goes to the
    // address it hands the page.
    retryTranscript: async (options) => {
      await request(() => connected().retry());
      return snapshot(options);
    },
    // The page's stream has no replay: a lost event ends in `resync`, which loads the session again.
    replay: async () => ({ events: [] }),
    contextWindow: () => request(() => connected().contextWindow(sessionId)),
    promptAuthor: () => LOCAL_SESSION_AUTHOR_ID,
    prompt: ({ turnId, text, delivery, skills, attachments }) =>
      request(async () => {
        const connection = connected();
        const messageId = localPromptId(turnId);
        const files = await promptFiles(attachments);
        // Once sent, a steer to an idle session starts a run of its own and makes it active.
        const workingBeforeSend =
          delivery === 'steer' &&
          (await connection.active(sessionId).catch((error: unknown) => {
            clientLogger.warn(
              'Could not read whether the agent on this computer works, so a steer goes without a cut-in',
              error,
            );
            return false;
          }));
        const createdAt = Date.now();
        let item: SessionInboxUser | SessionInboxCompaction | undefined;
        try {
          item = isCompactPrompt({ text, skills, attachments })
            ? await connection.compact(sessionId, { messageId, delivery })
            : await connection.prompt(sessionId, {
                messageId,
                text,
                delivery,
                files,
                ...(skills.length > 0 ? { skills } : {}),
              });
        } catch (error) {
          if (!isAlreadySent(error)) {
            const failure = toLocalRequestError(error);
            // OpenCode took the send and never finished answering: the first read begun after this
            // lists its prompt, or says OpenCode never got it. Sent again under the same id, the
            // prompt runs once: OpenCode answers with the copy that reached it late, if one did.
            if (failure?.kind === 'unconfirmed') {
              feed.unconfirmedSend({ turnId, text, delivery, createdAt, attachments });
              throw new UnconfirmedPromptError();
            }
            // A send that fails with an answer, or finds nothing answering, did not arrive: there is
            // no network between the page and loopback to lose a response on.
            throw failure ?? error;
          }
        }
        const turn = feed.sent({
          turnId,
          text,
          delivery,
          createdAt: item?.time?.created ?? Date.now(),
          attachments: heldPromptImages(item, attachments),
        });
        const cutIn =
          workingBeforeSend &&
          (await cutInForSteers(connection, sessionId).catch((error: unknown) => {
            clientLogger.warn(
              'A steer on this computer could not cut in and waits for the next step',
              error,
            );
            return false;
          }));
        return { turn, cutIn };
      }),
    steerTurn: (turnId) =>
      request(async () => {
        const connection = connected();
        const row = await rowFor(turnId);
        await connection.setDelivery(sessionId, localPromptId(turnId), 'steer');
        return { turn: { ...row, delivery: 'steer' } };
      }),
    cancelTurn: (turnId) =>
      request(async () => {
        const connection = connected();
        const row = await rowFor(turnId);
        await connection.cancelQueued(sessionId, localPromptId(turnId));
        // Discarded as well as ended, so the tray drops the row at once, as it drops a cloud one.
        const now = isoTime(Date.now());
        return {
          turn: { ...row, status: 'interrupted', endedAt: now, discardedAt: now },
        };
      }),
    // OpenCode's inbox keeps a prompt's words and place as sent: a queued follow-up can be steered or
    // removed, never changed or moved.
    editTurn: async () => notLocal('Editing a queued prompt'),
    moveTurn: async () => notLocal('Moving a queued prompt'),
    sendNow: () =>
      request(async () => ({ interrupted: await cutInForSteers(connected(), sessionId) })),
    interrupt: () => request(() => connected().interrupt(sessionId)),
    answer: (formId, reply) =>
      request(async () => {
        const connection = connected();
        if (isPermissionReply(formId, reply)) {
          // The consent guard holds only this session's requests, so the answer names this session.
          await connection.answerPermission(sessionId, formId, reply.permission);
        } else if ('cancel' in reply) {
          await connection.cancelForm(sessionId, formId);
        } else {
          await connection.answerForm(sessionId, formId, reply);
        }
        return { ok: true as const };
      }),
    // Adapted from OpenCode's packages/app/src/session/revert.ts (MIT, Copyright (c) 2025
    // opencode), commit 9b4ec5714d481559990db0a816d5dec19541a814.
    // OpenCode refuses to stage while a turn runs, and its interrupt answers before the turn has
    // ended: stop, wait, then stage. Both are no-ops on an idle session.
    stageRewind: (turnId, restoreFiles) =>
      request(async () => {
        const connection = connected();
        await connection.interrupt(sessionId);
        await connection.waitIdle(sessionId);
        // Cancel edit wakes the session, which would run the follow-ups waiting in its inbox, so
        // they go before the stage, as they do in the cloud. Removing one never wakes the session.
        const { session } = await snapshot();
        await Promise.all(
          session.turns
            .filter((turn) => turn.status === 'queued')
            .map((turn) => connection.cancelQueued(sessionId, localPromptId(turn.id))),
        );
        try {
          await connection.stageRevert(sessionId, {
            messageId: localPromptId(turnId),
            restoreFiles,
          });
        } catch (error) {
          // OpenCode keeps restoring files after the client gives up and records the revert once
          // they are back, so an answer that never came is settled by reading what it staged.
          if (
            !(error instanceof OpenCodeTimeoutError) ||
            (await snapshot()).session.rewindTurnId !== turnId
          ) {
            throw error;
          }
        }
        refreshLocalList();
        return { session: (await snapshot()).session };
      }),
    clearRewind: () =>
      request(async () => {
        await connected().clearRevert(sessionId);
        refreshLocalList();
        return { session: (await snapshot()).session };
      }),
    // OpenCode names the fork as it makes it, and makes another each time it is asked.
    fork: ({ beforeTurnId }) => ({
      forkId: null,
      make: () =>
        request(async () => {
          const forked = await connected().fork(sessionId, {
            before: beforeTurnId === undefined ? undefined : localPromptId(beforeTurnId),
          });
          return {
            session: localSessionRow({
              session: forked,
              turns: [],
              active: false,
              awaitingInput: false,
            }),
          };
        }),
    }),
    savedTurns: (options) =>
      request(async () => {
        options?.signal?.throwIfAborted();
        const taken = await connected().getSnapshot(sessionId);
        options?.signal?.throwIfAborted();
        const tail = { events: [], cursor: 0 };
        return { session: localSessionResponse(taken, localTurnRows(taken), tail).session };
      }),
  };
}

/**
 * How long to wait before asking again after an empty answer: OpenCode on this computer answers a
 * folder's first search before it has read the folder, and its skill list before the folder's
 * plugins have loaded them, which took up to 10 s after a start and up to 2 s for a folder new to
 * a running agent.
 */
const SEARCH_AGAIN_MS = [1_000];
const SKILLS_AGAIN_MS = [1_000, 2_000, 4_000];

async function readAgainIfEmpty<T>(
  read: () => Promise<T[]>,
  waits: readonly number[],
  options?: ReadOptions,
): Promise<T[]> {
  options?.signal?.throwIfAborted();
  let answer = await read();
  for (const wait of waits) {
    if (answer.length > 0) {
      break;
    }
    await new Promise((resolve) => setTimeout(resolve, wait));
    options?.signal?.throwIfAborted();
    answer = await read();
  }
  return answer;
}

/** The files and folders `@` lists, from the page's own client to OpenCode on this computer. */
function findLocalFiles(
  place: FileSearchPlace,
  query: string,
  options?: ReadOptions,
): Promise<WorkspaceSearchResult> {
  return request(async () => {
    const connection = connected();
    const entries = await readAgainIfEmpty(
      () => connection.findFiles(place, query),
      SEARCH_AGAIN_MS,
      options,
    );
    return { kind: 'ready', entries };
  });
}

/**
 * The checkout is on the person's disk: every read but a search says so, and nothing commits or
 * pushes from here.
 */
function localWorkspace(sessionId: string): SessionWorkspace {
  return {
    file: async () => ({ kind: 'unavailable', reason: 'sandbox-unavailable' }),
    changes: async () => ({ kind: 'unavailable', reason: 'sandbox-unavailable' }),
    diff: async () => ({ kind: 'unavailable', reason: 'sandbox-unavailable' }),
    gitStatus: async () => notLocal('Git status'),
    gitAction: async () => notLocal('Commit and push'),
    findFiles: (query, options) => findLocalFiles({ sessionId }, query, options),
  };
}

function localRecord(sessionId: string): SessionRecord {
  return {
    rename: (title) =>
      request(async () => {
        // An empty title has OpenCode name the session again with a model call.
        const saved = normalizeCodeSessionTitle(title);
        if (saved.length === 0) {
          throw localFailure('Session title cannot be empty', 400);
        }
        const held = await connected().renameSession(sessionId, saved);
        return { title: held.title, updatedAt: isoTime(held.updatedAt) };
      }),
    setApprovalMode: (mode) => request(() => connected().setApprovalMode(sessionId, mode)),
    regenerateTitle: () =>
      request(async () => {
        // An empty title has OpenCode name the session again, with a model call it awaits before it
        // answers: what OpenCode's own TUI sends to regenerate one.
        const held = await connected().renameSession(sessionId, '');
        // A generation that found nothing to name the session by leaves the title as it was,
        // which for a session OpenCode never titled is none.
        if (held.title === '') {
          throw localFailure('Could not generate a title', 502);
        }
        return { title: held.title, updatedAt: isoTime(held.updatedAt) };
      }),
    archive: () => request(async () => localSavedRow(await connected().archiveSession(sessionId))),
    unarchive: () =>
      request(async () => localSavedRow(await connected().unarchiveSession(sessionId))),
  };
}

/**
 * Every session on this computer, as the sidebar lists them. A session OpenCode
 * lists as active runs a turn, and waits on the person while a permission
 * request or a question is pending on it, marked from its last update as the
 * detail row marks it (`localSessionRow`); the rest read idle, and one cubic
 * archived reads archived. Its child sessions that run are its agents at work,
 * as a cloud session's helpers are, and no session here has a check-in. The one
 * person who writes here started each of them, so the sidebar files them under
 * theirs, as it files the detail row.
 */
export function listLocalSessions(options?: ReadOptions): Promise<CodeSessionSummary[]> {
  return request(async () => {
    options?.signal?.throwIfAborted();
    const sessions = await connected().listSessions();
    // A title, once OpenCode gives one, names the session for good. A session missing from this
    // read keeps its prompt: a read that began before its create would not list it yet.
    for (const session of sessions) {
      if (session.title !== null) {
        firstPrompts.delete(session.id);
      }
    }
    return sessions.map(localListRow);
  });
}

function localListRow(
  session: ListedSession,
): CodeSessionSummary & Pick<CodeSessionRow, 'createdByProfileId' | 'checkInDueAt'> {
  return {
    id: session.id,
    installationId: 0,
    repositoryId: 0,
    title:
      session.title ??
      untitledSessionName(folderName(session.folder), firstPrompts.get(session.id)),
    repoFullName: folderName(session.folder),
    sessionBranch: '',
    status: localStatus({ archived: session.archivedAt !== null, active: session.active }),
    sandboxStatus: 'ready',
    lastActivityAt: localActivityAt(session),
    updatedAt: isoTime(session.updatedAt),
    forkedFromSessionId: null,
    startedBySessionId: null,
    source: 'web',
    sourceUrl: null,
    mcpClientName: null,
    rewindTurnId: localRewindTurnId(session.revertMessageId),
    awaitingInputAt: session.awaitingInput ? isoTime(session.updatedAt) : null,
    pullRequest: null,
    createdByProfileId: LOCAL_SESSION_AUTHOR_ID,
    checkInDueAt: null,
    ...(session.agentsAtWork.length > 0 ? { agentsAtWork: session.agentsAtWork } : {}),
  };
}

function localSavedRow(session: LocalSessionSummary): SavedSessionRow {
  return {
    ...localListRow({ ...session, awaitingInput: false, agentsAtWork: [] }),
    sideAgentParentId: null,
  };
}

/**
 * Sessions on the person's own computer, through the page's own client to
 * the server the desktop plugin started (`connection.ts`).
 */
export const localRuntime: CodeRuntime & {
  /** Start a session in a folder with its first prompt. */
  createSession: (input: {
    folder: string;
    model: string;
    prompt: string;
    attachments: readonly CodePromptAttachment[];
    approvalMode: ApprovalMode;
  }) => Promise<Pick<SessionResponse, 'session' | 'imageRoom'>>;
  /** The files and folders in a folder a start chose that match `query`, for its `@`. */
  findFiles: (
    folder: string,
    query: string,
    options?: ReadOptions,
  ) => Promise<WorkspaceSearchResult>;
} = {
  session: (sessionId) => ({
    conversation: localConversation(sessionId),
    workspace: localWorkspace(sessionId),
    record: localRecord(sessionId),
    skills: (options) =>
      request(async () => {
        const connection = connected();
        const listed = await readAgainIfEmpty(
          () => connection.listSkills(sessionId),
          SKILLS_AGAIN_MS,
          options,
        );
        return { status: 'ready' as const, skills: codeSkillsOf(listed) };
      }),
    retry: (next, workKey) => startAgainFirst(sessionId, next, workKey),
  }),
  queue: 'inbox',
  words: {
    resuming: LOCAL_AGENT_STARTING,
    // A session on this computer goes by its folder until OpenCode titles it: the folder once, not
    // twice.
    ownTitle: ({ title, repoFullName }) => (title === repoFullName ? null : title),
  },
  forget: forgetLocalSessionFeed,
  createSession: ({ folder, model, prompt, attachments, approvalMode }) =>
    request(async () => {
      const connection = connected();
      // A session starts here only from This computer's own start, which may install the agent.
      await connection.ensure({ install: true });
      const files = await promptFiles(attachments);
      // OpenCode titles a session from its first message's words, so one of images alone would be
      // titled "New conversation".
      const title =
        prompt.length === 0 && attachments.length > 0
          ? normalizeCodeSessionTitle(codeSessionPromptTitle(prompt, attachments))
          : undefined;
      const created = await connection.createSession({ folder, model, title, approvalMode });
      const turnId = crypto.randomUUID();
      const messageId = localPromptId(turnId);
      const sentAt = Date.now();
      let item: SessionInboxUser | undefined;
      try {
        item = await connection.prompt(created.id, { messageId, text: prompt, files });
      } catch (error) {
        // A 409 says another copy of this prompt was delivered while this one was taken in, so the
        // session runs it, as a follow-up's does. A send the page made again after it started
        // OpenCode again lands here only then: OpenCode answers it with the first copy, if that
        // copy arrived.
        if (!isAlreadySent(error)) {
          const failure = toLocalRequestError(error);
          // OpenCode took the prompt and never finished answering, so the session may run it: it
          // stays, and its first read lists the prompt or carries it as a failed send, whose Retry
          // sends it again under the same id, as a follow-up's does.
          if (failure?.kind === 'unconfirmed') {
            localSessionFeed(created.id).unconfirmedSend({
              turnId,
              text: prompt,
              delivery: 'queue',
              createdAt: sentAt,
              attachments,
            });
          } else {
            // Creating the session and sending its first prompt is one step: a session whose
            // prompt never arrived is removed rather than left empty in the list, and the prompt's
            // failure is the one reported.
            await connection.deleteSession(created.id).catch(() => undefined);
            throw failure ?? error;
          }
        }
      }
      firstPrompts.set(created.id, codeSessionPromptTitle(prompt, attachments));
      // Once OpenCode took the prompt the session exists and runs it, so the create is done: the
      // row is what OpenCode answered the create with and the turn sent, with no read that could
      // fail and report a running session as one that was never created. The view that opens the
      // session reads it, and places its numbering itself.
      const turn = sentTurn({
        turnId,
        messageId,
        text: prompt,
        delivery: 'queue',
        index: 0,
        createdAt: item?.time?.created ?? Date.now(),
        attachments: heldPromptImages(item, attachments),
      });
      return {
        session: localSessionRow({
          session: created.session,
          turns: [turn],
          active: true,
          awaitingInput: false,
        }),
        // Until its first read, the first prompt's images count against the room.
        imageRoom: localImageRoom(0, [turn]),
      };
    }),
  findFiles: (folder, query, options) => findLocalFiles({ folder }, query, options),
};
